import React, { Fragment } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { CustomDialog } from '../common/custom-dialog';
import {
  AppBar,
  Box,
  makeStyles,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@material-ui/core';
import MapSearchIcon from '../../../../static/outageIconsV2/map_search.svg';
import colors from '../../../../themes/main-colors';
import LegendInfo from '../legend-info/LegendInfo';
import MapOptions from '../map-options/MapOptions';
import { toSentenceCase } from '../../../../util/format';

interface OutageMapDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  setValue?: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  tabsRoot: {
    width: '100%',
  },
  appBar: {
    backgroundColor: colors.white,
    padding: theme.spacing(0, 2.5),
    borderBottom: `2px solid ${colors.lightGray1}`,
    '& .MuiTab-root': {
      fontSize: '1rem',
      color: colors.black,
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
      fontWeight: 600,
    },
  },
}));

export const OutageMapDialog = ({
  open,
  setOpen,
  setValue: initialValue,
}: OutageMapDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [value, setValue] = React.useState(initialValue ?? 0);

  React.useEffect(() => {
    if (initialValue !== undefined) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Fragment>
      <CustomDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('OUTAGE_MAP')}
        titleIcon={<MapSearchIcon fontSize={30} />}
      >
        <div className={classes.tabsRoot}>
          <AppBar position="static" className={classes.appBar} elevation={0}>
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="primary"
              variant="fullWidth"
              aria-label="full width tabs example"
            >
              <Tab label={t('LEGEND_LABEL')} {...a11yProps(0)} />
              <Tab label={t('MAP_OPTIONS')} {...a11yProps(1)} />
            </Tabs>
          </AppBar>
          <TabPanel value={value} index={0}>
            <LegendInfo />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <MapOptions />
          </TabPanel>
        </div>
      </CustomDialog>
    </Fragment>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}
